"use client";

import React, { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { fetchTopProductCategories } from "@/api-service/category";
import { HomeCategories } from "@/packages/types/category";
import { Product, Variant } from "@/packages/types/products";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import QuickViewModal from "../productdetail/QuickViewModal";
import { Swiper, SwiperSlide } from "swiper/react";
import { Keyboard } from "swiper/modules";

import "swiper/css";

import { HEADER_V2_DESIGN_SYSTEM } from "../header/v2/designTokens";
import ProductCardV2 from "../product/ProductCardV2";
import { useResolvedCountryName } from "@/packages/hooks/useResolvedCountryName";
import { ProductGridSkeleton } from "../common/Skeletons";

interface TrendingProduct extends Product {
  variants?: Variant[];
}

const TrendingCategoriesV2 = () => {
  const countryName = useResolvedCountryName();
  const [categories, setCategories] = useState<HomeCategories[]>([]);
  const [activeCategory, setActiveCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const categoryListRef = useRef<HTMLDivElement>(null);
  const { fastPush } = useFastRouter();

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        setLoading(true);
        const res = await fetchTopProductCategories();
        if (isMounted) {
          if (res.status === "SUCCESS") {
            const fetchedCategories = res?.data?.categories || [];
            setCategories(fetchedCategories);
            if (fetchedCategories.length > 0) {
              setActiveCategory(fetchedCategories[0].id);
            }
          } else {
            toast.error(res.message || "Failed to load categories");
          }
        }
      } catch (error: unknown) {
        console.error("Error fetching categories:", error);
        if (isMounted) {
          toast.error("Error fetching categories");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  const scrollCategories = (direction: "left" | "right") => {
    if (categoryListRef.current) {
      const scrollAmount = 200;
      const currentScroll = categoryListRef.current.scrollLeft;
      const newPosition =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;
      categoryListRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });
    }
  };

  const handleQuickView = (product: Product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  if (!loading && categories.length === 0) {
    return null;
  }

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const activeProducts = (currentCategory?.products || []) as TrendingProduct[];

  return (
    <section className={`w-full max-w-7xl mx-auto my-8 md:my-12 ${HEADER_V2_DESIGN_SYSTEM.spacing.containerPadding}`}>
      {/* Title block */}
      <div className="mb-6 md:mb-8">
        <h2 className={`font-semibold tracking-tight ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilyHeading} ${HEADER_V2_DESIGN_SYSTEM.typography.size20} ${HEADER_V2_DESIGN_SYSTEM.colors.primaryDarkText}`}>
          Featured Products{countryName ? ` in ${countryName}` : ""}
        </h2>
        <p className={`mt-1.5 text-xs md:text-sm ${HEADER_V2_DESIGN_SYSTEM.colors.secondaryText} ${HEADER_V2_DESIGN_SYSTEM.typography.fontFamilySans}`}>
          Explore our trending items and carefully curated categories
        </p>
      </div>

      {loading ? (
        <div className="w-full space-y-6">
          <div className="hidden md:flex gap-2 pb-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-9 w-28 bg-slate-200 rounded-lg animate-pulse" />
            ))}
          </div>
          <ProductGridSkeleton count={5} />
        </div>
      ) : (
        <>
          {/* Mobile: Render Category Sections Row by Row */}
          <div className="block md:hidden space-y-8">
            {categories.map((category) => {
              const catProducts = (category.products || []) as TrendingProduct[];
              if (catProducts.length === 0) return null;

              return (
                <div key={category.id} className="space-y-4">
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-bold text-slate-900 text-sm tracking-tight">
                      {category.name}
                    </h3>
                    <button
                      onClick={() => fastPush(`/collections/${category.slug}`)}
                      className="text-xs font-semibold text-slate-800 underline hover:text-[#D97706] transition-colors"
                    >
                      See All
                    </button>
                  </div>

                  <div className="w-full !overflow-visible">
                    <Swiper
                      modules={[Keyboard]}
                      spaceBetween={12}
                      slidesPerView={2.2}
                      keyboard={{ enabled: true }}
                      className="w-full"
                    >
                      {catProducts.slice(0, 15).map((product, index) => (
                        <SwiperSlide key={product.id || index} className="h-auto">
                          <ProductCardV2
                            product={product}
                            onQuickView={() => handleQuickView(product)}
                            className="h-full"
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop view tabs navigation */}
          <div className="hidden md:flex mb-8 items-center gap-3 bg-slate-50 p-1.5 rounded-xl border border-slate-100">
            <button
              type="button"
              onClick={() => scrollCategories("left")}
              className="flex-shrink-0 rounded-lg p-1.5 hover:bg-slate-200 text-slate-600 transition-colors"
              aria-label="Scroll category tabs left"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div
              ref={categoryListRef}
              className="flex flex-1 gap-2 overflow-x-auto scrollbar-none py-0.5"
            >
              {categories.map((category) => {
                const isActive = activeCategory === category.id;
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`flex-shrink-0 px-4 py-2 font-semibold text-xs transition-all tracking-wide 
                      ${HEADER_V2_DESIGN_SYSTEM.borderRadius.btn}
                      ${
                        isActive
                          ? "bg-slate-900 text-white shadow-sm"
                          : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50/80"
                      }`}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => scrollCategories("right")}
              className="flex-shrink-0 rounded-lg p-1.5 hover:bg-slate-200 text-slate-600 transition-colors"
              aria-label="Scroll category tabs right"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Desktop view grid */}
          <div className="hidden md:block w-full">
            {activeProducts.length === 0 ? (
              <div className="h-[250px] flex items-center justify-center bg-slate-50/50 rounded-xl border border-slate-100">
                <p className="text-xs text-slate-400">No products found in this category</p>
              </div>
            ) : (
              <div className="grid gap-4 md:gap-6 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                {activeProducts.slice(0, 15).map((product, index) => (
                  <ProductCardV2
                    key={product.id || index}
                    product={product}
                    onQuickView={() => handleQuickView(product)}
                  />
                ))}
              </div>
            )}

            {/* Desktop Centered "View All" link */}
            {activeProducts.length > 0 && currentCategory && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => fastPush(`/collections/${currentCategory.slug}`)}
                  className={`text-sm font-semibold text-slate-900 underline hover:text-[#D97706] transition-colors`}
                >
                  View All
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Quick View Modal */}
      <QuickViewModal
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
        slug={selectedProduct?.slug}
      />
    </section>
  );
};

export default TrendingCategoriesV2;
